import { userProfileOptions } from '@doneverse/data/queries'
import { authService, uploadProfilePicture } from '@doneverse/data/services'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'

interface ProfileUpdates {
  name?: string
}

export const useUpdateProfileMutation = () => {
  const { updateProfile: updateLocalProfile, profile } = useAuth()

  return useMutation({
    mutationFn: async (updates: ProfileUpdates) => {
      if (!profile?.id) throw new Error('User not loaded')
      const { error } = await authService.updateProfile(profile.id, updates)
      if (error) throw error
      return updates
    },
    onSuccess: (variables) => {
      // Update local context to reflect changes immediately
      updateLocalProfile(variables)
      toast.success('Settings saved successfully!')
    },
    onError: (error) => {
      console.error('Error saving settings:', error)
      toast.error('Error saving settings')
    },
  })
}

export const useUploadProfilePictureMutation = () => {
  const { updateProfile: updateLocalProfile, profile } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const fileUrl = await uploadProfilePicture(file)
      return fileUrl
    },
    onSuccess: (fileUrl) => {
      updateLocalProfile({ profile_picture_url: fileUrl })
      queryClient.invalidateQueries({ queryKey: userProfileOptions(profile?.id ?? null).queryKey })
      toast.success('Profile picture updated!')
    },
    onError: (error) => {
      console.error('Error uploading profile picture:', error)
      toast.error('Failed to upload profile picture')
    },
  })
}

export const useRequestPasswordResetMutation = () => {
  return useMutation({
    mutationFn: async (email: string) => {
      return await authService.requestPasswordResetAuth0(email)
    },
    onSuccess: () => {
      toast.success('Password reset email sent!')
    },
    onError: (error) => {
      console.error('Failed to request password reset:', error)
      toast.error('Failed to request password reset', {
        description: error instanceof Error ? error.message : undefined,
      })
    },
  })
}
